/**
 * Student report prefetch — Experience Engine™ Flow A support.
 *
 * The Mirror Moment™ lasts ~6 seconds. We use that pause to fetch (or
 * assemble) the student report so that "Continue" lands instantly — a
 * spinner after the most emotional beat in the product is a broken
 * promise measured in milliseconds.
 *
 * Semantics:
 *  - `prefetchStudentReport(userId)` starts the fetch once and caches the
 *    in-flight promise. Failures are NOT cached, so the report page can
 *    retry cleanly.
 *  - `consumeStudentReport(userId)` returns the cached promise if one
 *    exists and removes it from the cache (single use — a later visit to
 *    /report always fetches fresh data, e.g. after a re-assessment).
 *
 * This module moves NO business logic — it is the exact fetch the report
 * page performed inline, made shareable. The payload remains the sealed
 * Experience Engine object, verbatim.
 */
import { supabase } from "@/integrations/supabase/client";
import { measure } from "@/lib/observability";

const cache = new Map<string, Promise<unknown>>();

/**
 * When the learner last FINISHED a v2 run. A sealed report older than this is
 * a report of a previous run: the card and the report would then contradict
 * the result screen, which is the one thing canon forbids. Returns null when
 * we can't tell — an unknown is never grounds for discarding a report.
 */
async function latestRunCompletedAt(userId: string): Promise<string | null> {
  const { data } = await supabase
    .from("runtime_sessions")
    .select("completed_at, metadata")
    .eq("learner_id", userId)
    .eq("status", "completed")
    .order("completed_at", { ascending: false })
    .limit(25);
  const run = (data ?? []).find((row) => {
    const meta = (row.metadata ?? {}) as Record<string, unknown>;
    return meta.source === "assessment-v2" && !meta.seeded_by;
  });
  return run?.completed_at ?? null;
}

async function fetchStudentReport(userId: string): Promise<unknown> {
  // Prefer the most recent persisted report — unless a newer run has since
  // been completed, in which case it is stale and gets re-assembled.
  const [{ data: row }, runAt] = await Promise.all([
    supabase
      .from("learner_reports")
      .select("payload, generated_at")
      .eq("learner_id", userId)
      .eq("audience", "student")
      .order("generated_at", { ascending: false })
      .limit(1)
      .maybeSingle(),
    latestRunCompletedAt(userId).catch(() => null),
  ]);
  const stale = Boolean(
    row?.generated_at && runAt && Date.parse(runAt) > Date.parse(row.generated_at),
  );
  if (row?.payload && !stale) return row.payload;

  // No persisted report — assemble one on demand. Measured: an assemble failure
  // is a blank Living Report™, and slow assembles are the post-assessment wait.
  const { data, error } = await measure("report", "report_assemble.invoke", () =>
    supabase.functions.invoke("report-assemble", {
      body: { audience: "student", persist: true },
    }),
  );
  if (error) {
    // A stale report still beats a blank page — show it rather than nothing.
    if (row?.payload) return row.payload;
    throw new Error(error.message);
  }
  return data;
}

export function prefetchStudentReport(userId: string): Promise<unknown> {
  const existing = cache.get(userId);
  if (existing) return existing;
  const p = fetchStudentReport(userId).catch((e) => {
    cache.delete(userId); // never cache a failure
    throw e;
  });
  cache.set(userId, p);
  return p;
}

export function consumeStudentReport(userId: string): Promise<unknown> {
  const existing = cache.get(userId);
  if (existing) {
    cache.delete(userId);
    return existing;
  }
  return fetchStudentReport(userId);
}
